"use client";

import React from 'react';
import Link from 'next/link';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; reset: () => void }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  showDetails?: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Call onError prop if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback component if provided
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} reset={this.reset} />;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md mx-auto text-center p-6">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-6">
              We encountered an unexpected error. Please try refreshing the page or going back to the homepage.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={this.reset}
                className="w-full px-6 py-3 bg-[#B980B8] text-white rounded-md hover:bg-[#A470A8] transition-colors"
              >
                Try Again
              </button>
              
              <Link
                href="/"
                className="block w-full px-6 py-3 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Go to Homepage
              </Link>
            </div>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.props.showDetails && this.state.error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md text-left">
                <h3 className="text-sm font-semibold text-red-800 mb-2">
                  Error Details (Development Only)
                </h3>
                <pre className="text-xs text-red-700 whitespace-pre-wrap overflow-auto">
                  {this.state.error.message}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easier usage
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  return function WithErrorBoundaryComponent(props: P) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}

// Specialized error fallback components
export function SectionErrorFallback({ error, reset }: { error?: Error; reset: () => void }) {
  return (
    <div className="py-20 px-4 text-center">
      <div className="max-w-md mx-auto">
        <div className="text-4xl mb-4">😵</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Section failed to load
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          This section encountered an error and couldn't be displayed.
        </p>
        <button
          onClick={reset}
          className="px-4 py-2 bg-[#B980B8] text-white rounded-md hover:bg-[#A470A8] transition-colors text-sm"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

export function ComponentErrorFallback({ error, reset }: { error?: Error; reset: () => void }) {
  return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-md">
      <div className="text-center">
        <div className="text-2xl mb-2">⚠️</div>
        <h4 className="text-sm font-semibold text-red-800 mb-1">
          Component Error
        </h4>
        <p className="text-xs text-red-700 mb-3">
          This component failed to render properly.
        </p>
        <button
          onClick={reset}
          className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

export default ErrorBoundary;